import { ProjetBadge } from "@/components/ProjetBadge";
import { fmtEur, fmtNum, fmtMwhc, SECTOR_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Euro, Gauge, Sparkles } from "lucide-react";

export function ResultSummary({ result }) {
  if (!result) return null;
  const ops = result.operations || [];

  return (
    <div className="space-y-5" data-testid="result-summary">
      {result.eligible === false && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="result-ineligible">{result.eligibility_message}</div>}
      <Card className="gradient-genis text-white p-6 overflow-hidden relative">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative grid grid-cols-1 gap-4 [&>div]:min-w-0">
          <div>
            <div className="flex items-center gap-1.5 text-white/80 text-xs font-semibold uppercase tracking-wider">
              <Euro className="h-3.5 w-3.5" /> Prime estimée
            </div>
            <div className="metric text-2xl font-extrabold mt-1 break-words" data-testid="metric-total-prime-eur">{fmtEur(result.total_prime)}</div>
            {result.options_count > 0 && <div className="text-white/70 text-xs mt-0.5">hors options</div>}
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-white/80 text-xs font-semibold uppercase tracking-wider">
              <Gauge className="h-3.5 w-3.5" /> Volume total
            </div>
            <div className="metric text-2xl font-extrabold mt-1 break-words" data-testid="metric-total-mwhc">{fmtMwhc(result.total_kwhc)}</div>
            <div className="text-white/70 text-xs metric mt-0.5">{fmtNum(result.total_kwhc)} kWh cumac</div>
          </div>
        </div>
        {result.options_count > 0 && (
          <div className="relative mt-4 rounded-lg bg-white/15 p-3 text-xs space-y-1" data-testid="options-totals">
            <div className="flex justify-between gap-2"><span>Options ({result.options_count})</span><span className="metric font-semibold" data-testid="metric-options-prime">+ {fmtEur(result.options_prime)}</span></div>
            <div className="flex justify-between gap-2 font-bold text-sm"><span>Total avec options</span><span className="metric" data-testid="metric-total-with-options">{fmtEur(result.total_with_options_prime)}</span></div>
            <div className="text-right text-white/70 metric">{fmtMwhc(result.total_with_options_kwhc)}</div>
          </div>
        )}
        <div className="relative mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-white/20 px-2.5 py-1 font-medium">Zone {result.zone}</span>
          <span className="rounded-full bg-white/20 px-2.5 py-1 font-medium">{result.cee_price} €/MWhc</span>
          {result.cpe_enabled && (
            <span className="rounded-full bg-white/25 px-2.5 py-1 font-semibold inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> CPE ×{result.cpe_coefficient}
            </span>
          )}
          {result.zni_enabled && (
            <span className="rounded-full bg-white/25 px-2.5 py-1 font-semibold inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> ZNI ×{result.zni_coefficient}
            </span>
          )}
        </div>
        <div className="relative mt-3 text-[11px] text-white/75 leading-snug" data-testid="prime-disclaimer">
          Prix de rachat indicatif, non contractuel — contactez Genis Energie pour un prix ferme et définitif (variable selon le volume de CEE, les dates de chantier, le type de fiche et le secteur d'activité).
        </div>
      </Card>

      {result.by_block && result.by_block.length > 1 && (
        <Card className="p-5">
          <h4 className="font-heading font-bold text-genis-deep dark:text-white mb-3">Répartition par secteur</h4>
          <div className="space-y-2">
            {result.by_block.map((b, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`recap-block-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: SECTOR_META[b.sector]?.color || "#33ADF5" }} />
                  <span className="font-semibold text-sm truncate">{b.block_name}</span>
                  <span className="text-xs text-muted-foreground">({b.count})</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="metric font-bold text-genis">{fmtEur(b.prime)}</div>
                  <div className="metric text-xs text-muted-foreground">{fmtNum(b.kwhc)} kWhc</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {result.has_precarite && (
        <Card className="p-5" data-testid="precarite-split">
          <h4 className="font-heading font-bold text-genis-deep dark:text-white mb-1">Répartition CEE classique / précarité</h4>
          <p className="text-xs text-muted-foreground mb-3">Département {result.department} · taux précarité (colonne A) {result.precarite_pct}% · prix précarité {fmtEur(result.cee_price_precaire)}/MWhc</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3" data-testid="split-classique">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">CEE classique</div>
              <div className="metric font-bold text-genis-deep dark:text-white mt-1">{fmtEur(result.total_prime_classique)}</div>
              <div className="metric text-xs text-muted-foreground">{fmtNum(result.total_kwhc_classique)} kWhc</div>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3" data-testid="split-precaire">
              <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">CEE précarité</div>
              <div className="metric font-bold text-indigo-700 mt-1">{fmtEur(result.total_prime_precaire)}</div>
              <div className="metric text-xs text-indigo-500">{fmtNum(result.total_kwhc_precaire)} kWhc</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5">
        <h4 className="font-heading font-bold text-genis-deep dark:text-white mb-3">Détail par opération</h4>
        {ops.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune opération ajoutée.</p>
        ) : (
          <div className="space-y-3">
            {ops.map((o, i) => {
              const meta = SECTOR_META[o.sector];
              return (
                <div key={i} className={`rounded-lg border p-3 ${o.is_option ? "border-dashed border-amber-300 bg-amber-50/50" : "border-border"}`} data-testid={`result-op-${o.fiche_code}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={`${meta.badge} border text-[10px]`}>{o.fiche_code}</Badge>
                        <ProjetBadge show={o.projet} code={o.fiche_code} />
                        {o.is_option && <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px] shrink-0" data-testid={`result-op-option-${i}`}>Option</Badge>}
                      </div>
                      <div className="font-semibold text-sm mt-1 leading-snug">{o.op_name || o.name}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {o.op_name ? o.name : null}{o.op_name && o.block_name ? " · " : ""}{o.block_name}
                        {o.steps_count > 1 ? `${(o.op_name || o.block_name) ? " · " : ""}${o.steps_count} étapes` : ""}
                      </div>
                      {o.bonus_labels?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {o.bonus_labels.map((b, k) => (
                            <span key={k} className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${b.includes("non appliqué") ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}>{b}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="metric font-bold text-genis">{fmtEur(o.prime)}</div>
                      <div className="metric text-xs text-muted-foreground">{fmtNum(o.kwhc)} kWhc</div>
                    </div>
                  </div>
                  <div className="mt-2 grid gap-0.5 text-[11px] text-muted-foreground border-t border-border pt-2">
                    {o.steps?.map((s, k) => (
                      <div key={k} className="flex justify-between gap-2">
                        <span>{s.label}</span>
                        <span className="metric font-medium text-foreground">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="pt-2 space-y-1 text-sm border-t border-border">
              <div className="flex justify-between text-muted-foreground" data-testid="recap-base-kwhc">
                <span>Volume de base (avant bonifications)</span>
                <span className="metric">{fmtNum(result.base_kwhc_total)} kWhc</span>
              </div>
              {result.cdp_bonus_kwhc > 0 && (
                <div className="flex justify-between text-green-700 font-medium" data-testid="recap-cdp-bonus">
                  <span>Bonification Coup de pouce</span>
                  <span className="metric">+ {fmtNum(result.cdp_bonus_kwhc)} kWhc</span>
                </div>
              )}
              {result.cpe_bonus_kwhc > 0 && (
                <div className="flex justify-between text-bonus-cpe font-medium" data-testid="recap-cpe-bonus">
                  <span>Bonification CPE (×{result.cpe_coefficient})</span>
                  <span className="metric">+ {fmtNum(result.cpe_bonus_kwhc)} kWhc</span>
                </div>
              )}
              {result.zni_bonus_kwhc > 0 && (
                <div className="flex justify-between text-pink-600 font-medium" data-testid="recap-zni-bonus">
                  <span>Bonification ZNI</span>
                  <span className="metric">+ {fmtNum(result.zni_bonus_kwhc)} kWhc</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-genis-deep dark:text-white pt-1">
                <span>{result.options_count > 0 ? "Total ferme (hors options)" : "Total"}</span>
                <span className="metric">{fmtNum(result.total_kwhc)} kWhc</span>
              </div>
              {result.options_count > 0 && (
                <>
                  <div className="flex justify-between text-amber-700 font-medium">
                    <span>Options ({result.options_count})</span>
                    <span className="metric">+ {fmtNum(result.options_kwhc)} kWhc</span>
                  </div>
                  <div className="flex justify-between font-bold text-genis-deep dark:text-white">
                    <span>Total avec options</span>
                    <span className="metric">{fmtNum(result.total_with_options_kwhc)} kWhc</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
